import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TextField } from '../@core/components/mui';
import { PageListLayout } from '../components/layouts/PageListLayout';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { investigationService } from '../services/investigationService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';
import {
  IncidentSeverities,
  InvestigationStatuses,
  type IncidentInvestigationSessionDto,
  type InvestigationFilters,
  type IncidentSeverity,
  type InvestigationStatus,
} from '../types/investigation';

const severityLabel = (severity: IncidentSeverity): string => {
  switch (severity) {
    case IncidentSeverities.Critical:
      return 'Critical';
    case IncidentSeverities.High:
      return 'High';
    case IncidentSeverities.Medium:
      return 'Medium';
    case IncidentSeverities.Low:
      return 'Low';
    default:
      return 'Unknown';
  }
};

const severityColor = (
  severity: IncidentSeverity
): 'default' | 'success' | 'info' | 'warning' | 'error' => {
  switch (severity) {
    case IncidentSeverities.Critical:
      return 'error';
    case IncidentSeverities.High:
      return 'warning';
    case IncidentSeverities.Medium:
      return 'info';
    case IncidentSeverities.Low:
      return 'success';
    default:
      return 'default';
  }
};

const statusLabel = (status: InvestigationStatus): string => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'Completed';
    case InvestigationStatuses.InProgress:
      return 'In Progress';
    case InvestigationStatuses.Paused:
      return 'Paused';
    case InvestigationStatuses.Cancelled:
      return 'Cancelled';
    case InvestigationStatuses.NotStarted:
      return 'Not Started';
    default:
      return 'Unknown';
  }
};

const statusColor = (
  status: InvestigationStatus
): 'default' | 'success' | 'info' | 'warning' | 'error' => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'success';
    case InvestigationStatuses.InProgress:
      return 'info';
    case InvestigationStatuses.Paused:
      return 'warning';
    case InvestigationStatuses.Cancelled:
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Investigation list page component (Module 24).
 */
export const InvestigationListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [investigations, setInvestigations] = useState<IncidentInvestigationSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<InvestigationFilters>({
    pageNumber: 1,
    pageSize: 10,
    searchTerm: '',
    investigationStatus: undefined,
    severity: undefined,
  });

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 10));

  const severityOptions = useMemo(
    () => [
      { value: IncidentSeverities.Low, label: 'Low' },
      { value: IncidentSeverities.Medium, label: 'Medium' },
      { value: IncidentSeverities.High, label: 'High' },
      { value: IncidentSeverities.Critical, label: 'Critical' },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: InvestigationStatuses.NotStarted, label: 'Not Started' },
      { value: InvestigationStatuses.InProgress, label: 'In Progress' },
      { value: InvestigationStatuses.Paused, label: 'Paused' },
      { value: InvestigationStatuses.Completed, label: 'Completed' },
      { value: InvestigationStatuses.Cancelled, label: 'Cancelled' },
    ],
    []
  );

  const loadInvestigations = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await investigationService.getInvestigations(filters);
      setInvestigations(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load investigations';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvestigations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNumber, filters.pageSize, filters.searchTerm, filters.investigationStatus, filters.severity]);

  const handleComplete = async (id: string) => {
    try {
      await investigationService.completeInvestigation(id);
      showNotification('Investigation completed successfully', 'success');
      await loadInvestigations();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to complete investigation';
      showNotification(msg, 'error');
    }
  };

  const tableColumns: DataTableColumn<IncidentInvestigationSessionDto>[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 220,
      render: (inv) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {inv.title}
          </Typography>
          {inv.description && (
            <Typography variant="caption" color="text.secondary">
              {inv.description.length > 80 ? `${inv.description.slice(0, 80)}…` : inv.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'severity',
      label: 'Severity',
      minWidth: 120,
      render: (inv) => <Chip label={severityLabel(inv.severity)} color={severityColor(inv.severity)} size="small" />,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 130,
      render: (inv) => <Chip label={statusLabel(inv.investigationStatus)} color={statusColor(inv.investigationStatus)} size="small" />,
    },
    {
      id: 'incidentNumber',
      label: 'Incident #',
      minWidth: 120,
      render: (inv) => <Typography variant="body2">{inv.incidentNumber || '-'}</Typography>,
    },
    {
      id: 'started',
      label: 'Started',
      minWidth: 170,
      render: (inv) =>
        inv.investigationStartedAt ? (
          <Typography variant="body2">{new Date(inv.investigationStartedAt).toLocaleString()}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      id: 'completed',
      label: 'Completed',
      minWidth: 170,
      render: (inv) =>
        inv.investigationCompletedAt ? (
          <Typography variant="body2">{new Date(inv.investigationCompletedAt).toLocaleString()}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      id: 'counts',
      label: 'Counts',
      minWidth: 160,
      render: (inv) => (
        <Typography variant="body2" color="text.secondary">
          Steps: {inv.stepCount} • Msg: {inv.messageCount} • Corr: {inv.correlationCount}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 160,
      align: 'right',
      render: (inv) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => navigate(`/investigations/${inv.id}`)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/investigations/${inv.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {inv.investigationStatus !== InvestigationStatuses.Completed && (
            <Tooltip title="Complete">
              <IconButton size="small" onClick={() => handleComplete(inv.id)}>
                <CompleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Incident Investigations"
      createButton={{
        label: 'Create Investigation',
        onClick: () => navigate(ROUTES.INVESTIGATION_NEW),
        icon: <AddIcon />,
      }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 220 }}
            placeholder="Title, description, incident #"
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.investigationStatus !== undefined ? String(filters.investigationStatus) : ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  investigationStatus:
                    String(e.target.value) === '' ? undefined : (Number(e.target.value) as InvestigationStatus),
                  pageNumber: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={filters.severity !== undefined ? String(filters.severity) : ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  severity:
                    String(e.target.value) === '' ? undefined : (Number(e.target.value) as IncidentSeverity),
                  pageNumber: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              {severityOptions.map((opt) => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={!loading && investigations.length === 0}
      emptyState={{
        title: 'No investigations found',
        description: 'Create a new investigation to get started.',
        action: {
          label: 'Create Investigation',
          onClick: () => navigate(ROUTES.INVESTIGATION_NEW),
        },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages,
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={investigations}
      />
    </PageListLayout>
  );
};

